import {
  defaultListTitle,
  type ListFont,
  type ListOccasion,
  type ListTemplate,
  type ListTheme,
  type WishPriority,
} from "@listou/shared/constants";
import type { PublicList, PublicWish } from "@listou/shared/types/api";

import { motifOf, packStickerUrls } from "./theme";

/**
 * Example lists for the landing page and /exemple. They go through the real ListPage, so
 * what a visitor sees there is exactly what their family would open.
 */

type DemoWish = [
  title: string,
  priceCents: number | null,
  priority: WishPriority,
  taken?: boolean | null,
  note?: string | null,
  imageUrl?: string,
];

const wishesOf = (items: DemoWish[]): PublicWish[] =>
  items.map(([title, priceCents, priority, taken, note, imageUrl]) => ({
    id: `demo-${title}`,
    title,
    note: note ?? null,
    imageUrl: imageUrl ?? null,
    priceCents,
    currency: "EUR",
    priority,
    quantity: 1,
    reservedQuantity: taken ? 1 : 0,
    merchant: null,
    // No real shop behind an example: a "Voir sur" button would lead nowhere.
    goUrl: null,
    productSlug: null,
  }));

const listOf = (input: {
  slug: string;
  occasion: ListOccasion;
  template: ListTemplate;
  theme: ListTheme;
  pack: string;
  font?: ListFont;
  motif?: "semis" | "dense" | "diagonale" | "bordure";
  recipientName: string | null;
  ownerNames: string[];
  eventDate: string | null;
  description: string | null;
}): PublicList => ({
  slug: input.slug,
  title: input.recipientName ?? input.ownerNames.join(" & "),
  description: input.description,
  occasion: input.occasion,
  eventDate: input.eventDate,
  recipientName: input.recipientName,
  headline: defaultListTitle(input.occasion, input.recipientName ?? input.ownerNames.join(" & ")),
  ownerNames: input.ownerNames,
  fundUrl: null,
  theme: input.theme,
  accentColor: null,
  emoji: null,
  coverUrl: null,
  appearance: {
    template: input.template,
    font: input.font ?? null,
    motif: input.motif ? motifOf(input.pack, input.motif) : null,
    stickerPack: input.pack,
    stickers: packStickerUrls(input.pack).map((url, index) => ({ id: String(index), url })),
  },
  shareImageUrl: "",
  hasAffiliateLinks: true,
});

const nextDecember = () => {
  const now = new Date();
  const year =
    now.getMonth() === 11 && now.getDate() > 25 ? now.getFullYear() + 1 : now.getFullYear();
  return `${year}-12-25`;
};

export type DemoList = { list: PublicList; wishes: PublicWish[] };

export const DEMO_LISTS: Record<ListTemplate, DemoList> = {
  pop: {
    list: listOf({
      slug: "leo",
      occasion: "birthday",
      template: "pop",
      theme: "kids",
      pack: "dinos",
      motif: "semis",
      recipientName: "Léo",
      ownerNames: ["Julie", "Karim"],
      eventDate: null,
      description: "Léo fête ses 6 ans ! Il est à fond dans les dinosaures en ce moment.",
    }),
    wishes: wishesOf([
      ["Le grand squelette de T-rex à monter", 3490, "love"],
      ["Lampe de poche projecteur d'étoiles", 1990, "want", true],
      ["Livre « Les dinosaures en pop-up »", 1650, "want"],
      ["Tente d'explorateur", 4500, "idea"],
    ]),
  },
  carnet: {
    list: listOf({
      slug: "noel-chez-nous",
      occasion: "christmas",
      template: "carnet",
      theme: "christmas",
      pack: "noel",
      recipientName: "Rose",
      ownerNames: ["Camille"],
      eventDate: nextDecember(),
      description:
        "Rose a 4 ans et adore peindre. Pas de plastique qui clignote, s'il vous plaît !",
    }),
    wishes: wishesOf([
      ["Chevalet en bois double face", 5900, "love", true],
      ["Coffret de gouaches", 2400, "want"],
      ["Tablier de peintre", 1500, "want"],
      ["Un livre de contes d'hiver", null, "idea"],
    ]),
  },
  "faire-part": {
    list: listOf({
      slug: "claire-et-antoine",
      occasion: "wedding",
      template: "faire-part",
      theme: "wedding",
      pack: "mariage",
      recipientName: null,
      ownerNames: ["Claire", "Antoine"],
      eventDate: "2027-06-19",
      description:
        "Votre présence est notre plus beau cadeau. Pour le reste, voici quelques idées.",
    }),
    wishes: wishesOf([
      ["Une nuit à Lisbonne pour notre voyage", 12000, "love", true],
      ["Service à thé en grès", 6800, "want"],
      ["Cours de cuisine à deux", 9000, "idea"],
    ]),
  },
  vitrine: {
    list: listOf({
      slug: "naissance-lou",
      occasion: "birth",
      template: "vitrine",
      theme: "birth",
      pack: "jardin",
      motif: "semis",
      recipientName: "Lou",
      ownerNames: ["Inès", "Tom"],
      eventDate: null,
      description:
        "Lou arrive au printemps. On a surtout besoin du pratique, et d'un peu de douceur.",
    }),
    wishes: wishesOf([
      ["Écharpe de portage", 7900, "love"],
      ["Body en coton bio, 6 mois", 1800, "want", true],
      ["Mobile en feutrine", 3500, "want"],
      ["Veilleuse lapin", 2900, "idea"],
    ]),
  },
  mosaique: {
    list: listOf({
      slug: "maya-30-ans",
      occasion: "birthday",
      template: "mosaique",
      theme: "birth",
      pack: "jardin",
      recipientName: "Maya",
      ownerNames: ["Maya"],
      eventDate: null,
      description: "Trente ans, un nouvel appartement et quelques envies pour le remplir.",
    }),
    wishes: wishesOf([
      ["Fauteuil en rotin", 18900, "love", null, "Celui du salon de Nina, en version claire."],
      ["Vase en grès", 4200, "want"],
      [
        "Cours de céramique",
        6500,
        "want",
        null,
        "Un samedi matin, n'importe quel atelier du coin.",
      ],
      ["Plaid en laine", 7900, "want", true],
      ["Un beau livre de cuisine italienne", 3500, "idea"],
      ["Lampe à poser", 8900, "want", null, "Pour le coin lecture, plutôt en lumière chaude."],
      ["Carafe en verre", 2800, "idea"],
      ["Set de couteaux", 12000, "love"],
      ["Plante grimpante", 3000, "want", true],
    ]),
  },
  epure: {
    list: listOf({
      slug: "mes-envies",
      occasion: "other",
      template: "epure",
      theme: "minimal",
      pack: "foret",
      recipientName: null,
      ownerNames: ["Paul"],
      eventDate: null,
      description: null,
    }),
    wishes: wishesOf([
      ["Carnet de randonnée", 1900, "love"],
      ["Gourde isotherme", 2500, "want"],
      ["Chaussettes en laine", 1400, "idea", true],
    ]),
  },
};

const SAMPLE_BY_OCCASION: Record<ListOccasion, DemoList> = {
  birthday: DEMO_LISTS.pop,
  christmas: DEMO_LISTS.carnet,
  wedding: DEMO_LISTS["faire-part"],
  // Six, not four: the creation preview opens on a grid, and two rows fill its frame.
  birth: {
    ...DEMO_LISTS.vitrine,
    wishes: wishesOf([
      ["Écharpe de portage", 7900, "love"],
      ["Body en coton bio, 6 mois", 1800, "want", true],
      ["Mobile en feutrine", 3500, "want"],
      ["Gigoteuse en gaze de coton", 3900, "want"],
      ["Veilleuse lapin", 2900, "idea"],
      ["Mes premiers imagiers", 1500, "idea"],
    ]),
  },
  other: DEMO_LISTS.epure,
};

/** A few example wishes for the creation preview, before the list has any of its own. */
export const sampleWishes = (occasion: ListOccasion): PublicWish[] =>
  SAMPLE_BY_OCCASION[occasion].wishes;

/** The list being created, as its public page would show it. */
export const sampleList = (
  occasion: ListOccasion,
  input: { title: string; recipientName: string | null; eventDate: string | null },
): PublicList => ({
  ...SAMPLE_BY_OCCASION[occasion].list,
  title: input.title,
  occasion,
  recipientName: input.recipientName,
  eventDate: input.eventDate,
  description: null,
  ownerNames: [],
  headline: input.title,
});

/**
 * One believable example per occasion landing, dressed in that occasion's look. These go
 * through the real ListPage, so the landing shows the page a family would actually open.
 */
export const OCCASION_DEMOS: Record<Exclude<ListOccasion, "other">, DemoList> = {
  birth: {
    list: listOf({
      slug: "bienvenue-lou",
      occasion: "birth",
      template: "faire-part",
      theme: "birth",
      pack: "bebe",
      motif: "semis",
      recipientName: "Lou",
      ownerNames: ["Inès", "Tom"],
      eventDate: null,
      description: "Lou arrive au printemps. Surtout du pratique, et un peu de douceur.",
    }),
    // Generated pictures, not shop photos: no rights to clear, no brand on the landing.
    wishes: wishesOf([
      [
        "Gigoteuse en gaze de coton, 0-6 mois",
        3900,
        "love",
        true,
        null,
        "/demo/naissance/gigoteuse.webp",
      ],
      ["Poussette canne légère", 19900, "love", null, null, "/demo/naissance/poussette.webp"],
      ["Body en coton bio, taille 6 mois", 1800, "want", true, null, "/demo/naissance/body.webp"],
      ["Mobile en feutrine", 3500, "want", null, null, "/demo/naissance/mobile.webp"],
      ["Mes premiers imagiers", 1500, "idea", null, null, "/demo/naissance/imagiers.webp"],
    ]),
  },
  christmas: {
    list: listOf({
      slug: "noel-de-leo",
      occasion: "christmas",
      template: "carnet",
      theme: "christmas",
      pack: "noel",
      recipientName: "Léo",
      ownerNames: ["Camille"],
      eventDate: nextDecember(),
      description: "Léo a 7 ans. Il rêve de construire et de lire sous la couette.",
    }),
    wishes: wishesOf([
      ["Lego Creator, le camion de pompiers", 4999, "love", true],
      ["Lampe frontale pour lire la nuit", 1990, "want"],
      ["Bande dessinée « Les Cahiers d'Esther »", 1495, "want"],
      ["Pyjama en flanelle, 8 ans", 2490, "idea", true],
    ]),
  },
  birthday: {
    list: listOf({
      slug: "les-6-ans-de-leo",
      occasion: "birthday",
      template: "pop",
      theme: "kids",
      pack: "dinos",
      motif: "semis",
      recipientName: "Léo",
      ownerNames: ["Julie", "Karim"],
      eventDate: null,
      description: "Léo fête ses 6 ans ! En ce moment, c'est dinosaures à tous les étages.",
    }),
    wishes: wishesOf([
      ["Squelette de T-rex à monter", 3490, "love"],
      ["Kit de fouilles « fossiles »", 1890, "want", true],
      ["Lampe projecteur d'étoiles", 1990, "want"],
      ["Livre « Les dinosaures en pop-up »", 1650, "idea"],
    ]),
  },
  wedding: {
    list: listOf({
      slug: "claire-et-antoine",
      occasion: "wedding",
      template: "faire-part",
      theme: "wedding",
      pack: "mariage",
      recipientName: null,
      ownerNames: ["Claire", "Antoine"],
      eventDate: "2027-06-19",
      description:
        "Votre présence est notre plus beau cadeau. Pour le reste, voici quelques idées.",
    }),
    wishes: wishesOf([
      ["Une nuit à Lisbonne pour le voyage de noces", 12000, "love", true],
      ["Service à thé en grès", 6800, "want"],
      ["Robot pâtissier", 34900, "want"],
      ["Cours de cuisine à deux", 9000, "idea"],
    ]),
  },
};
